import R, { filter } from 'ramda';
import { fromJS } from 'immutable';
import uuid from 'node-uuid';
const insertSeasonsToSeasonRates = (immutableProduct, seasons) => {
  const basedSeasons = seasons || immutableProduct.get('seasons').toJS();
  return immutableProduct.set('seasonRates',
    fromJS(basedSeasons.map((season, i) => {
      const seasonRate = immutableProduct.getIn(['seasonRates', i]);
      if (seasonRate) return R.merge(seasonRate.toJS(), season);
      return R.merge(season, { rateSets: [] });
    })));
};

const policiesInCaps = {
  cancellation: 'Cancellation',
  group: 'Group',
  freeOfCharge: 'Free of Charge',
  family: 'Family',
  child: 'Child',
  infant: 'Infant',
};
const editProduct = (initialProduct, payload) => {
  switch (payload.updateType) {
    case 'DELETE_PRODUCT_POLICY_FROM_CONTRACT':
      return {
        hash: { 'hash.productPolicy': uuid.v1() },
        section: 'policy',
        product: initialProduct.update('productPolicy', filter(policy => policy.get('policyType') !== payload.policyType)),
        editHistory: { detail: `Deleted ${payload.policyType}` },
      };
    case 'DELETE_SEASON_FROM_PRODUCT':
      const contractingPeriod = [{ seasonStartDate: payload.contractingPeriod.startDate, seasonEndDate: payload.contractingPeriod.endDate }];
      const deleteFromSeasonalArrFn = arr => {
        if (arr.size > 1 && arr.size === initialProduct.get('seasons').size) {
          const deleted = arr.delete(payload.seasonRatesIndex);
          if (deleted.size === 1) return deleted.setIn([0, 'dateRanges'], fromJS(contractingPeriod));
          return deleted;
        }
        return arr;
      };
      return {
        hash: {
          'hash.seasons': uuid.v1(),
          'hash.rates': uuid.v1(),
        },
        section: 'seasons',
        product: initialProduct.deleteIn(['seasonRates', payload.seasonRatesIndex])
          .deleteIn(['seasons', payload.seasonRatesIndex])
          .update('allocation', deleteFromSeasonalArrFn)
          .update('pickup', deleteFromSeasonalArrFn)
          .update('dropoff', deleteFromSeasonalArrFn)
          .update('productPolicy', productPolicy => productPolicy.map(policy => {
            if (policy.get('policyType') === 'cancellation') {
              return policy.update('cancellations', deleteFromSeasonalArrFn);
            }
            return policy;
          })),
        editHistory: { detail: `Deleted ${
          initialProduct.getIn(['seasons', payload.seasonRatesIndex, 'seasonName'])
            ? `${initialProduct.getIn(['seasons', payload.seasonRatesIndex, 'seasonName'])} Season`
            : 'A Season'}` },
      };
    case 'DELETE_RATE_SET_FROM_SEASON':
      return {
        hash: { 'hash.rates': uuid.v1() },
        section: 'rates',
        product: initialProduct.deleteIn(['seasonRates', payload.seasonRatesIndex, 'rateSets', payload.rateSetsIndex]),
        editHistory: { detail: `Deleted A Rate Set ${
          initialProduct.getIn(['seasons', payload.seasonRatesIndex, 'seasonName'])
            ? `from ${initialProduct.getIn(['seasons', payload.seasonRatesIndex, 'seasonName'])} Season`
            : ''}` },
      };
    case 'SEASONRATES_SAVED_TO_DB':
      return {
        hash: {
          'hash.seasons': uuid.v1(),
          'hash.rates': uuid.v1(),
        },
        section: 'seasons',
        product: initialProduct.update('seasonRates', oldSeasonRates => oldSeasonRates
          ? oldSeasonRates.push(fromJS(payload.seasonRates))
          : fromJS([payload.seasonRates]))
          .update('seasons', seasons => seasons.push(fromJS(R.pick(['seasonName', 'dateRanges'], payload.seasonRates)))),
        editHistory: { detail: `Added Season and Rates ${
          payload.seasonRates.seasonName
            ? `- ${payload.seasonRates.seasonName} Season`
            : ''}` },
      };
    case 'SAVE_PRODUCT_CODE_ALLOCATION':
      return {
        hash: { 'hash.allocation': uuid.v1() },
        section: 'allocation',
        product: initialProduct.merge(fromJS(payload.data)),
        editHistory: { detail: 'Updated Allocation Information' },
      };
    case 'SAVE_SEASON_DATES_CHANGES':
      const sortByStartDateAsc = R.sort(R.cond([
        [R.useWith(R.lt, [R.prop('seasonStartDate'), R.prop('seasonEndDate')]), R.always(-1)],
        [R.useWith(R.gt, [R.prop('seasonStartDate'), R.prop('seasonEndDate')]), R.always(1)],
        [R.T, R.always(0)],
      ]));
      const datesSortedSeasons = payload.data.existingSeasons.map(R.mapObjIndexed((val, key) => {
        if (key === 'dateRanges') {
          return sortByStartDateAsc(val.filter(dateRange => dateRange.seasonStartDate));
        }
        return val;
      }));
      return {
        hash: { 'hash.seasons': uuid.v1() },
        section: 'seasons',
        product: insertSeasonsToSeasonRates(initialProduct.set('seasons', fromJS(datesSortedSeasons)), datesSortedSeasons),
        editHistory: { detail: 'Updated Season Information' },
      };
    case 'SAVE_RATES_CHANGES_ADD_EDIT':
      /*
       seasons OR seasonRates, whichever one is longer array should be considered as the base array
       */
      // seasonRates is longer array
      // need to make sure seasons match with seasonRates
      if (initialProduct.get('seasonRates').filter(x => x).size > initialProduct.get('seasons').filter(x => x).size) {
        return {
          hash: { 'hash.rates': uuid.v1() },
          section: 'rates',
          editHistory: { detail: 'Updated Rate Set' },
          product: initialProduct.update('seasonRates', seasonRates => {
            return seasonRates.filter(x => x).map((seasonRate, thisIndex) => {
              if (thisIndex === payload.data.seasonRatesIndex) {
                return seasonRate.set('rateSets', fromJS(payload.data.rateSets));
              }
              return seasonRate;
            });
          })
          .update('seasons', () => initialProduct.get('seasonRates')),
        };
      }
      // seasons is longer array
      // this will make sure seasonRates' length match seasons
      return {
        hash: { 'hash.rates': uuid.v1() },
        section: 'rates',
        editHistory: { detail: `Updated Rate Set ${
          initialProduct.getIn(['seasons', payload.data.seasonRatesIndex, 'seasonName'])
          ? `for ${initialProduct.getIn(['seasons', payload.data.seasonRatesIndex, 'seasonName'])} Season`
          : ''}` },
        product: initialProduct.update('seasonRates', seasonRates => {
          return initialProduct.get('seasons').filter(x => x).map((season, thisIndex) => {
            if (thisIndex === payload.data.seasonRatesIndex) {
              return season.set('rateSets', fromJS(payload.data.rateSets));
            }
            const seasonRateObject = seasonRates.get(thisIndex);
            if (seasonRateObject) return seasonRateObject;
            return season;
          });
        }),
      };
    case 'SAVED_BLACKOUT_DAYS':
      const blackouts = payload.data.blackouts.filter(blackout => blackout.startDate);
      return {
        hash: { 'hash.blackouts': uuid.v1() },
        section: 'blackouts',
        product: initialProduct.set('blackouts', fromJS(blackouts)),
        editHistory: { detail: `${(initialProduct.toJS().blackouts || []).length < blackouts.length ? 'Added' : 'Updated'} Blackout Days` },
      };
    case 'SAVED_PRODUCT_POLICY':
      const productPolicy = [...payload.data.productPolicy, payload.data.selectedPolicy];
      return {
        hash: { 'hash.productPolicy': uuid.v1() },
        section: 'policy',
        product: initialProduct.set('productPolicy', fromJS(productPolicy)),
        editHistory: { detail: `Added ${policiesInCaps[payload.data.selectedPolicy.policyType] || 'Product'} Policy` },
      };
    case 'SAVED_EDITED_PRODUCT_POLICY':
      const udpatedProductPolicy = payload.data.productPolicy.map(policy => {
        if (policy.policyType === payload.data.selectedPolicy.policyType) return payload.data.selectedPolicy;
        return policy;
      });
      return {
        hash: { 'hash.productPolicy': uuid.v1() },
        section: 'policy',
        product: initialProduct.set('productPolicy', fromJS(udpatedProductPolicy)),
        editHistory: { detail: `Updated ${policiesInCaps[payload.data.selectedPolicy.policyType] || 'Product'} Policy` },
      };
    case 'DELETE_OFFER_FROM_PRODUCT':
      return {
        hash: { 'hash.offers': uuid.v1() },
        section: 'offers',
        product: initialProduct.deleteIn(['offers', payload.offerIndex]),
        editHistory: { detail: 'Deleted An Special Offer' },
      };
    case 'DELETE_EXTRA_FROM_PRODUCT':
      return {
        hash: { 'hash.extras': uuid.v1() },
        section: 'extras',
        product: initialProduct.deleteIn(['extras', payload.extraIndex]),
        editHistory: { detail: 'Deleted An Extra' },
      };
    case 'SAVE_PICKUP_DROPOFF_INFO':
      return {
        hash: { 'hash.pickupDropoff': uuid.v1() },
        section: payload.data.pickupOrDropoff,
        product: initialProduct.set(payload.data.pickupOrDropoff, fromJS(payload.data[payload.data.pickupOrDropoff])),
        editHistory: { detail: 'Updated Pickup and Dropoff Information' },
      };
    case 'SAVE_SEASONAL_CANCELLATION_POLICIES':
      const updateCxl = (initialProduct.toJS().productPolicy || []).some(policy => policy.policyType === 'cancellation');
      const cancellationPolciesObj = R.pick(['cancellations', 'policyType', 'cancellations', 'dateRanges'], payload.data);
      return {
        hash: { 'hash.productPolicy': uuid.v1() },
        section: 'cancellations',
        product: initialProduct.update('productPolicy', policy => {
          if (!(policy && policy.length)) return fromJS([cancellationPolciesObj]);
          if (policy.some(obj => obj.get('policyType') === 'cancellation')) {
            return policy.map(obj => {
              if (obj.get('policyType') === 'cancellation') return fromJS(cancellationPolciesObj);
              return obj;
            });
          }
          return policy.concat(fromJS([cancellationPolciesObj]));
        }),
        editHistory: { detail: `${updateCxl ? 'Updated' : 'Added'} Cancellation Policies` },
      };
    default:
      return {
        product: initialProduct,
      };
  }
};

export default editProduct;



// WEBPACK FOOTER //
// ./src/app/stores/contracting/contract-activities-products/edit-product.js